import React, { useEffect, useRef } from 'react';
import { X, Star, Layers, Palette } from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { useCollapsedSections } from '../../hooks/useCollapsedSections';
import { useTheme } from '../../contexts/ThemeProvider';

function ThemeLabel({ theme }) {
  if (theme === 'dark') return 'Mörkt';
  if (theme === 'contrast') return 'Kontrast';
  return 'Ljust';
}

export function SettingsModal({ open, onClose }) {
  const { resetToDefaults: resetFavorites } = useFavorites();
  const { resetToDefaults: resetCollapsed } = useCollapsedSections();
  const { theme } = useTheme();
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.activeElement;
    closeBtnRef.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prev && typeof prev.focus === 'function') {
        try { prev.focus(); } catch {}
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="kp-settings-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kp-settings-title"
    >
      <div className="kp-settings-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="kp-settings">
        <div className="kp-settings-header">
          <h2 id="kp-settings-title">Inställningar</h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="kp-settings-close"
            onClick={onClose}
            aria-label="Stäng"
          >
            <X size={15} strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>
        <div className="kp-settings-body">
          <section className="kp-settings-row">
            <span className="kp-settings-row-icon" aria-hidden="true">
              <Palette size={16} strokeWidth={2} />
            </span>
            <div className="kp-settings-row-text">
              <div className="kp-settings-row-title">Tema</div>
              <div className="kp-settings-row-meta">
                Aktivt: <strong><ThemeLabel theme={theme} /></strong>. Tema växlas via
                ikonen längst ner i sidomenyn (ljust / mörkt / kontrast).
              </div>
            </div>
          </section>

          <section className="kp-settings-row">
            <span className="kp-settings-row-icon" aria-hidden="true">
              <Star size={16} strokeWidth={2} />
            </span>
            <div className="kp-settings-row-text">
              <div className="kp-settings-row-title">Favoriter</div>
              <div className="kp-settings-row-meta">
                Återställ favoriter till KanPlans standardlista
                (Dashboard, Uppgifter, Skyddsronder, Tidsredovisning).
              </div>
            </div>
            <button
              type="button"
              className="kp-settings-row-btn"
              onClick={() => {
                resetFavorites();
                onClose();
              }}
            >
              Återställ
            </button>
          </section>

          <section className="kp-settings-row">
            <span className="kp-settings-row-icon" aria-hidden="true">
              <Layers size={16} strokeWidth={2} />
            </span>
            <div className="kp-settings-row-text">
              <div className="kp-settings-row-title">Sektioner</div>
              <div className="kp-settings-row-meta">
                Återställ vilka sidebar-sektioner som är hopfällda till
                standardläget (Administration är hopfälld, övriga öppna).
              </div>
            </div>
            <button
              type="button"
              className="kp-settings-row-btn"
              onClick={() => {
                resetCollapsed();
                onClose();
              }}
            >
              Återställ
            </button>
          </section>
        </div>
        <div className="kp-settings-footer">
          <button
            type="button"
            className="kp-settings-footer-btn"
            onClick={onClose}
          >
            Stäng
          </button>
        </div>
      </div>
    </div>
  );
}
